"use client";

import { FeatureHeroSection } from "@/components/shared/feature_hero_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { DynamicEvents } from "@/components/shared/dynamic_events";
import { ContentDownload } from "@/components/shared/content_download";
import { get__related_how_we_help_pages } from "@/cms/how-we-help-data";
import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CustomForm } from "@/components/forms";
import { useTranslations } from "next-intl";

const Neuroscience = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalTriggerRef = useRef<HTMLButtonElement>(null);
    
    const t = useTranslations();

    // Handle focus management when modal closes
    useEffect(() => {
        console.log('Modal state changed:', isModalOpen);
        if (!isModalOpen && modalTriggerRef.current) {
            setTimeout(() => {
                modalTriggerRef.current?.focus();
            }, 100);
        }
    }, [isModalOpen]);
    return (
        <main className="flex justify-center" role="main" aria-label="StudyStash Neuroscience Feature">
            <div className="flex w-full flex-col">
                {/* Hero Section */}
                <FeatureHeroSection
                    icon="/icon-pack/StudyStash - Icon - Doc.png"
                    iconAlt="Neuroscience Icon"
                    category="neuroscience"
                    title="Adaptive learning intelligence "
                    highlightedText="built on proven neuroscience"
                    description="We’ve cracked the code on combining cognitive science with AI-powered learning by partnering with EdCortex, neuroscience specialists, to embed proven learning psychology into every interaction."
                    buttonText="Download the paper"
                    buttonHref="#"
                    showDemoModal={false}
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                    backgroundImage="/how-we-help/neuroscience/hero_the_neuroscience.png"
                    backgroundImageAlt="StudyStash Neuroscience Dashboard"
                    mobileImage="/how-we-help/neuroscience/hero_the_neuroscience.png"
                    mobileImageAlt="StudyStash Neuroscience Dashboard"
                />

                {/* Alternating Sections */}
                <div className="pt-32 md:pt-48 lg:pt-96">
                    <AlternatingSection 
                        sections={[
                            {
                                subtitle: "Backed by true learning science",
                                title: "Built with pedagogy and neuroscience",
                                content: (
                                    <>
                                        <p className="mb-4">
                                        StudyStash is designed with the principles of pedagogy and digital education at its core. By working with EdCortex - a company led by PhD researchers in neuroscience - our innovative platform incorporates the latest research into cognitive learning.
                                        </p>
                                    </>
                                ),
                                video: {
                                    src: "/how-we-help/neuroscience/StudyStash - EdCortex.mp4",
                                    alt: "Demonstration of learning principles in action",
                                }
                            },
                            {
                                subtitle: "For when generic AI is not enough",
                                title: "Our science-backed approach",
                                content: (
                                    <>
                                        <p>
                                            Methods like dual coding, 
                                            cognitive load management, and desirable difficulties help learners manage their workload and promotes genuine knowledge retention.
                                        </p>
                                    </>
                                ),
                                image: {
                                    src: "/how-we-help/neuroscience/spaced_repetition.png",
                                    alt: "Illustration of cognitive science learning methods",
                                }
                            },
                            {
                                subtitle: "StudyStash learns as you learn",
                                title: "Adaptive personalisation",
                                content: (
                                    <>
                                       <p>
  Over time, StudyStash learns each student’s weak areas, shares them with teachers, and adapts the flashcards, tests, and chats to create a {t("general.words.personalised")} learning path for every student.
</p>

                                    </>
                                ),
                                video: {
                                    src: "/how-we-help/neuroscience/the_algorithms.gif",
                                    alt: "Visualisation of adaptive algorithms guiding learner progress",
                                    type: "gif"
                                }
                            },                            
                        ]}
                    />
                </div>

                {/* Neuroscience Paper Download */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <ContentDownload
                        title="Download Our Neuroscience Research Paper"
                        description="Dive deep into the science behind StudyStash. Our comprehensive research paper details how we've integrated cutting-edge neuroscience principles into every aspect of our platform to optimise learning and retention."
                        buttonText="Book a demo"
                        buttonHref="/book-a-demo"
                        showSingleButton={false}
                        imageSrc="/Brochure-Mockup.png"
                        imageAlt="StudyStash Neuroscience Research Paper"
                        className="mb-16 md:mb-40"
                        onDownloadClick={() => setIsModalOpen(true)}
                    />
                </div>

                {/* Download Modal */}
                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <DialogContent className="sm:max-w-[600px] max-h-[95vh] overflow-y-auto">
                        <div className="mt-6">
                            <CustomForm formId="d1004-download-neuroscience-perspective" />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Related How We Help Pages */}
                <DynamicEvents 
                    items={get__related_how_we_help_pages("/how-we-help/neuroscience")}
                    showViewAll={false}
                    showKeepReading={true}
                />
            </div>
        </main>
    );
};

export default Neuroscience;
